import { RouteFocus } from "@clones/ui";
import { useRouterState } from "@tanstack/react-router";
import { ErrorState, Spinner } from "@clones/ui";
import { createRootRoute, createRoute, createRouter, Outlet, lazyRouteComponent } from "@tanstack/react-router";
import { RespondentAppShell } from "@/components/app-shell";
import { RespondentChatGate } from "@/runtime/feature-gate";

import { NotFoundPage } from "@/pages/not-found";

/**
 * Flat route tree. The whole app sits behind `RespondentChatGate` (runtime
 * feature flag); the authenticated chat route additionally wraps in
 * `RespondentAppShell` (which redirects to /login when the cookie session is
 * missing). Public auth pages skip the shell but still honour the gate so a
 * gated-off deployment shows a single consistent "unavailable" screen.
 */

const LoginPage = lazyRouteComponent(() => import("@/pages/login"), "LoginPage");
const RegisterPage = lazyRouteComponent(() => import("@/pages/register"), "RegisterPage");
const VerifyPage = lazyRouteComponent(() => import("@/pages/verify"), "VerifyPage");
const MfaPage = lazyRouteComponent(() => import("@/pages/mfa"), "MfaPage");
const ChatPage = lazyRouteComponent(() => import("@/pages/chat"), "ChatPage");

function RootContent() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const settled = useRouterState({ select: (state) => !state.isLoading && state.status === "idle" });
  return <><RouteFocus pathname={pathname} settled={settled} /><Outlet /></>;
}

const rootRoute = createRootRoute({
  component: RootContent,
  notFoundComponent: NotFoundPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: () => (
    <RespondentChatGate>
      <LoginPage />
    </RespondentChatGate>
  ),
  validateSearch: (search: Record<string, unknown>): { redirect?: string; verified?: string } => ({
    redirect: typeof search["redirect"] === "string" ? search["redirect"] : undefined,
    // TanStack coerces ?verified=1 to the NUMBER 1, so coerce to string for the
    // login banner's `=== "1"` / `=== "0"` comparison against the backend
    // verify-link redirect (ADR-0043).
    verified:
      search["verified"] === undefined || search["verified"] === null
        ? undefined
        : String(search["verified"]),
  }),
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/register",
  component: () => (
    <RespondentChatGate>
      <RegisterPage />
    </RespondentChatGate>
  ),
});

const verifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify",
  component: () => (
    <RespondentChatGate>
      <VerifyPage />
    </RespondentChatGate>
  ),
});

const mfaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mfa",
  component: () => (
    <RespondentChatGate>
      <MfaPage />
    </RespondentChatGate>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => (
    <RespondentChatGate>
      <RespondentAppShell>
        <ChatPage />
      </RespondentAppShell>
    </RespondentChatGate>
  ),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  registerRoute,
  verifyRoute,
  mfaRoute,
  indexRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPendingMs: 200,
  defaultPendingComponent: () => <div className="grid min-h-[40vh] place-items-center"><Spinner size="lg" /></div>,
  defaultErrorComponent: () => <ErrorState onRetry={() => window.location.reload()} />,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
